package com.patronat.top.client.presentation.root

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import com.patronat.top.client.presentation.auth.ClientAuthRoute
import com.patronat.top.client.presentation.main.ClientMainScreen
import com.patronat.top.client.presentation.onboarding.ClientOnboardingScreen
import com.patronat.top.client.presentation.splash.ClientSplashScreen
import com.patronat.top.core.auth.TokenStorage
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.ui.molecules.alerts.AlertUiModel
import com.patronat.top.ui.molecules.alerts.GlobalAlertOverlay
import com.patronat.top.ui.molecules.alerts.GlobalAlertStyle
import com.patronat.top.ui.theme.White
import com.russhwolf.settings.Settings
import com.russhwolf.settings.set
import kotlinx.coroutines.delay
import org.koin.compose.koinInject

@Composable
fun ClientRoot(
    webMode: Boolean = false
) {
    val alertController: AlertController = koinInject()
    val tokenStorage: TokenStorage = koinInject()
    val settings: Settings = koinInject()
    val notification by alertController.notification.collectAsState()
    var rootStep by rememberSaveable { mutableStateOf<ClientRootStep?>(null) }

    LaunchedEffect(tokenStorage, webMode) {
        if (webMode) {
            rootStep = ClientRootStep.Main
            return@LaunchedEffect
        }

        val hasAccessToken = !tokenStorage.getAccessToken().isNullOrBlank()
        val storedHasSeenIntro = settings.getBoolean(KEY_HAS_SEEN_INTRO, false)
        val hasSeenIntro = storedHasSeenIntro || hasAccessToken

        if (hasAccessToken && !storedHasSeenIntro) {
            settings[KEY_HAS_SEEN_INTRO] = true
        }

        rootStep = when {
            hasAccessToken -> ClientRootStep.Main
            hasSeenIntro -> ClientRootStep.Auth
            else -> ClientRootStep.Splash
        }

        if (!hasAccessToken && !hasSeenIntro) {
            delay(SPLASH_DURATION_MS)
            rootStep = ClientRootStep.Onboarding
        }
    }

    val alertData = notification?.let { current ->
        AlertUiModel(
            title = current.title.asString(),
            message = current.message.asString()
        )
    }

    GlobalAlertOverlay(
        alertData = alertData,
        style = GlobalAlertStyle.MobileBanner
    ) {
        when (rootStep) {
            null -> Box(
                modifier = androidx.compose.ui.Modifier
                    .fillMaxSize()
                    .background(White)
            )
            ClientRootStep.Splash -> ClientSplashScreen()
            ClientRootStep.Onboarding -> ClientOnboardingScreen(
                onSkip = {
                    settings[KEY_HAS_SEEN_INTRO] = true
                    rootStep = ClientRootStep.Auth
                },
                onFinished = {
                    settings[KEY_HAS_SEEN_INTRO] = true
                    rootStep = ClientRootStep.Auth
                }
            )
            ClientRootStep.Auth -> ClientAuthRoute(
                onContinueWithoutAccount = {
                    settings[KEY_HAS_SEEN_INTRO] = true
                    rootStep = ClientRootStep.Main
                },
                onAuthenticated = {
                    settings[KEY_HAS_SEEN_INTRO] = true
                    rootStep = ClientRootStep.Main
                }
            )
            ClientRootStep.Main -> ClientMainScreen(
                webMode = webMode,
                onAuthRequested = {
                    rootStep = ClientRootStep.Auth
                }
            )
        }
    }
}

private const val SPLASH_DURATION_MS = 900L
private const val KEY_HAS_SEEN_INTRO = "client_has_seen_intro"

private enum class ClientRootStep {
    Splash,
    Onboarding,
    Auth,
    Main
}
